import { ApiError } from '../identity/types';
import { friendlyErrorMessage } from '../../shared/friendlyError';
import type { ExportPreview } from './exportTypes';

/** The stable problem codes of the Accounting export API (contract section 15). Logic only ever reads the status and these codes, never message text. */
export const ExportErrorCode = {
  ValidationFailed: 'EXPORT_VALIDATION_FAILED',
  NotFound: 'EXPORT_NOT_FOUND',
  IntegrityError: 'EXPORT_INTEGRITY_ERROR',
  InvalidTransition: 'INVALID_EXPORT_TRANSITION',
  PayrollUnchanged: 'PAYROLL_UNCHANGED',
  PayrollNotApproved: 'PAYROLL_NOT_APPROVED',
  AccountArchived: 'ACCOUNT_ARCHIVED',
  AccountNumberExists: 'ACCOUNT_NUMBER_EXISTS',
  MappingInvalid: 'MAPPING_INVALID',
  ChartImportInvalid: 'CHART_IMPORT_INVALID',
  PerEmployeeNotPermitted: 'PER_EMPLOYEE_NOT_PERMITTED',
  // Mapping options, settings, employee groups and bulk code mappings (docs/payroll-accounting-export-mapping-options-contract.md section 6)
  OptionInvalid: 'OPTION_INVALID',
  DefaultWageAccountRequired: 'DEFAULT_WAGE_ACCOUNT_REQUIRED',
  VersionConflict: 'VERSION_CONFLICT',
  DuplicateName: 'DUPLICATE_NAME',
  EmployeeNotFound: 'EMPLOYEE_NOT_FOUND',
  GroupNotFound: 'GROUP_NOT_FOUND',
  OverlappingMembership: 'OVERLAPPING_MEMBERSHIP',
  IdempotencyKeyReused: 'IDEMPOTENCY_KEY_REUSED',
  ConfirmRequired: 'CONFIRM_REQUIRED',
  CodeMappingConflict: 'CODE_MAPPING_CONFLICT',
  GroupInvalid: 'GROUP_INVALID',
  EmployeeDirectoryUnavailable: 'EMPLOYEE_DIRECTORY_UNAVAILABLE',
} as const;

/** A generate request the server refused with its full validation report (HTTP 422). Nothing was created. */
export class ExportValidationError extends Error {
  constructor(public readonly report: ExportPreview, message = 'The export was blocked by validation. Nothing was created.') {
    super(message);
    this.name = 'ExportValidationError';
  }
}

const RAW_FALLBACK = /^Request to .+ failed with \d+$/;

/** A 403 for a missing PERMISSION. A subscription refusal (`SUBSCRIPTION_UPGRADE_REQUIRED`, `SUBSCRIPTION_REQUIRED`, `SUBSCRIPTION_ACCESS_RESTRICTED`) is also a 403 but is
 * about the company's plan, not the person's role, so it is never reported as "you do not have permission". */
export const isForbidden = (err: unknown): boolean => err instanceof ApiError && err.status === 403 && !(err.code ?? '').startsWith('SUBSCRIPTION');
export const isNotFound = (err: unknown): boolean => err instanceof ApiError && err.status === 404;
export const hasCode = (err: unknown, code: string): boolean => err instanceof ApiError && err.code === code;

export const FORBIDDEN_MESSAGE = 'You do not have permission to do that. Ask a company administrator if you need access to accounting exports.';

/**
 * The export API refuses a rule or state problem as a 400/409 with a hand-authored, user-safe `detail` (a duplicate account number, a payroll that
 * has not changed, an export that cannot move to that state). The shared helper replaces every 409 with a generic line, which would hide that
 * guidance, so those two statuses show the server's detail here; a 403 gets one calm sentence; everything else (401, 429, 5xx, unknown) goes
 * through the shared helper unchanged, which includes the support reference for a server failure.
 */
export function exportErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ExportValidationError) return err.message;
  if (err instanceof ApiError && (err.status === 400 || err.status === 409) && err.message && !RAW_FALLBACK.test(err.message)) return err.message;
  if (isForbidden(err)) return FORBIDDEN_MESSAGE;
  return friendlyErrorMessage(err, fallback);
}

/** The support reference of a server failure, when the response carried one (never fabricated). */
export function operationIdOf(err: unknown): string | null {
  return err instanceof ApiError ? err.operationId : null;
}
